/**
 * StewardshipTab — "Conductor" (iter86.52) · Platform Console main tab
 * where registry DECISIONS get made.
 *
 * The successor to the Guidance tab. Read-only detail lives in the
 * Values / Obligations tabs; this tab is the steward's work surface:
 *
 *   - Actions — the queue grouped New / Amended / Removed. "New" is the
 *     interactive pending-classification review (same
 *     PendingClassificationList as the Release Ops Values Engine card).
 *   - Audit — the append-only classification trail from
 *     `app/memory/values_registry_audit.md`, newest first.
 *
 * Future: "Map a Check" intake — classify newly-added signals/CI scripts
 * to a Value or Obligation before they land in a registry.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardList, Handshake, History, Link2, Radio, RefreshCw, Scale, ShieldAlert, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FailureRadarCard } from "@/components/site-admin/FailureRadarCard";
import { PendingClassificationList } from "@/components/site-admin/PendingClassificationList";
import { PulseDispositionsView } from "@/components/site-admin/PulseDispositionsView";
import { SeverityGovernanceView } from "@/components/site-admin/SeverityGovernanceView";
import { SubTabs, RegistryRow } from "@/components/site-admin/values-registry/RegistryShared";
import { TechDebtDispositionView } from "@/components/site-admin/TechDebtDispositionView";
import { TclMappingView } from "@/components/site-admin/TclMappingView";
import { VendorQueueView } from "@/components/site-admin/VendorQueueView";

// iter86.27 — Pulse follow-ups: reject/investigate briefing signals land
// here as workable items until resolved (with a note) or the signal flips
// to approve (auto-resolve).
function PulseFollowupsCard() {
    const [rows, setRows] = useState(null);
    const [me, setMe] = useState(null);
    const load = () => api.pulseFollowups("open").then((r) => setRows(r.followups)).catch(() => setRows([]));
    useEffect(() => { load(); api.authMe().then(setMe).catch(() => {}); }, []);
    // iter86.31.18 — resolution authority: Owner or the original filer only.
    const canResolve = (fu) => !!me && (me.is_super || (fu.user_email || "").toLowerCase() === (me.email || "").toLowerCase());
    const resolve = async (fu) => {
        const note = window.prompt(`Resolution note for "${fu.domain}" (iter${fu.iter})?`);
        if (!note) return;
        try {
            await api.pulseFollowupResolve(fu.id, { note });
            toast.success("Follow-up resolved");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Resolve failed");
        }
    };
    if (!rows?.length) return null;
    return (
        <div className="border border-amber-800 rounded p-3 mb-4 space-y-2 bg-amber-950/10" data-testid="pulse-followups-card">
            <p className="font-bold text-amber-200 text-xs uppercase">Pulse follow-ups ({rows.length} open)</p>
            <p className="text-[10px] text-zinc-500">Briefing sections signalled investigate/reject. The Commons Owner or the original filer resolves with a note, or the item auto-resolves when the reviewer's signal changes to approve.</p>
            {rows.map((fu) => (
                <div key={fu.id} className="flex items-center gap-2 flex-wrap text-[11px] border-t border-zinc-800 pt-1.5" data-testid={`pulse-followup-row-${fu.id}`}>
                    <span className={`pill text-[9px] font-bold uppercase border ${fu.signal === "reject" ? "bg-red-900/40 text-red-300 border-red-500" : "bg-amber-900/40 text-amber-300 border-amber-500"}`}>{fu.signal}</span>
                    <span className="font-bold text-white">{fu.domain}</span>
                    <span className="text-zinc-500">iter{fu.iter} · {fu.user_email}</span>
                    {fu.note && <span className="italic text-zinc-400">— {fu.note}</span>}
                    <div className="flex-1" />
                    {canResolve(fu) ? (
                        <button onClick={() => resolve(fu)} className="brutal-btn text-[10px] px-2 py-1"
                            data-testid={`pulse-followup-resolve-${fu.id}`}>Resolve</button>
                    ) : (
                        <span className="text-[9px] text-zinc-600 italic" title="Only the Commons Owner or the original filer can resolve this"
                            data-testid={`pulse-followup-locked-${fu.id}`}>Owner / filer only</span>
                    )}
                </div>
            ))}
        </div>
    );
}

function ActionsView({ data, reload }) {
    const [audit, setAudit] = useState(null);

    useEffect(() => {
        api.releaseOpsValuesRegistryAudit()
            .then((r) => setAudit(r.entries || []))
            .catch(() => setAudit([]));
    }, [data]);

    const definedGroups = Object.keys(data.value_groups || {}).filter(
        (s) => data.value_groups[s]?.statement
    );
    const retired = ["values_engine", "release_ops"].flatMap((root) =>
        ["crew_level", "game_specific"].flatMap((bucket) =>
            (data[root]?.[bucket] ?? []).filter((r) => r.status === "retired")
        )
    );
    const amended = (audit || []).filter(
        (a) => a.action === "amended" || a.action === "classified-override"
    );

    const Section = ({ title, count, tone, children, testId }) => (
        <section className={`border-2 rounded-lg p-4 ${tone}`} data-testid={testId}>
            <header className="flex items-center gap-2 mb-3">
                <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-zinc-200">
                    {title}
                </h3>
                <span className="text-[10px] text-zinc-500">({count})</span>
            </header>
            {children}
        </section>
    );

    return (
        <div className="space-y-6" data-testid="guidance-actions-view">
            <FailureRadarCard />
            <PulseFollowupsCard />
            <Section
                title="New — pending classification"
                count={data.pending_entries?.length ?? 0}
                tone="border-amber-900/50 bg-amber-950/10"
                testId="actions-section-new"
            >
                <PendingClassificationList
                    entries={data.pending_entries}
                    valueGroups={definedGroups}
                    onChanged={reload}
                />
            </Section>

            <Section
                title="Amended — classification changed"
                count={amended.length}
                tone="border-sky-900/50 bg-sky-950/10"
                testId="actions-section-amended"
            >
                {audit === null ? (
                    <p className="text-[11px] text-zinc-500 italic">Loading audit trail…</p>
                ) : amended.length === 0 ? (
                    <p className="text-[11px] text-zinc-500 italic">No overrides or amendments yet.</p>
                ) : (
                    <div className="space-y-1.5">
                        {amended.map((a, i) => (
                            <div
                                key={`${a.entry}-${a.timestamp}-${i}`}
                                className="border border-zinc-800 rounded p-2 text-[11px] bg-black/40 flex items-center gap-2 flex-wrap"
                                data-testid={`actions-amended-${a.entry}`}
                            >
                                <code className="font-bold text-white">{a.entry}</code>
                                <span className="text-zinc-500">{a.from || "—"}</span>
                                <span className="text-zinc-600">→</span>
                                <code className="text-fuchsia-300">{a.to || "—"}</code>
                                <span className="pill text-[9px] font-bold uppercase border bg-sky-900/40 text-sky-200 border-sky-700">
                                    {a.action}
                                </span>
                                <span className="ml-auto text-[9px] text-zinc-600 font-mono">{a.timestamp}</span>
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            <Section
                title="Removed — retired entries"
                count={retired.length}
                tone="border-rose-900/50 bg-rose-950/10"
                testId="actions-section-removed"
            >
                {retired.length === 0 ? (
                    <p className="text-[11px] text-zinc-500 italic">No retired entries.</p>
                ) : (
                    <div className="space-y-2">
                        {retired.map((row) => (
                            <RegistryRow key={row.name} row={row} showValueGroup />
                        ))}
                    </div>
                )}
            </Section>
        </div>
    );
}

function AuditView() {
    const [audit, setAudit] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.releaseOpsValuesRegistryAudit()
            .then((r) => setAudit(r.entries || []))
            .catch((e) => setError(e?.response?.data?.detail || "Failed to load audit trail"));
    }, []);

    if (error) {
        return <div className="text-xs text-rose-300" data-testid="audit-error">{error}</div>;
    }
    if (audit === null) {
        return <p className="text-[11px] text-zinc-500 italic" data-testid="audit-loading">Loading audit trail…</p>;
    }

    return (
        <div className="space-y-3" data-testid="guidance-audit-view">
            <div className="text-[10px] text-zinc-600">
                Append-only. Source: <code>app/memory/values_registry_audit.md</code> — newest first.
            </div>
            {audit.length === 0 ? (
                <p className="text-[11px] text-zinc-500 italic" data-testid="audit-empty">
                    No classification actions recorded yet.
                </p>
            ) : (
                <div className="overflow-x-auto border border-zinc-800 rounded-lg">
                    <table className="w-full text-[11px]">
                        <thead>
                            <tr className="text-left text-[9px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                                <th className="p-2">Timestamp</th>
                                <th className="p-2">Actor</th>
                                <th className="p-2">Entry</th>
                                <th className="p-2">Action</th>
                                <th className="p-2">From → To</th>
                                <th className="p-2">Note</th>
                            </tr>
                        </thead>
                        <tbody>
                            {audit.map((a, i) => (
                                <tr
                                    key={`${a.entry}-${a.timestamp}-${i}`}
                                    className="border-b border-zinc-900 last:border-b-0"
                                    data-testid={`audit-row-${i}`}
                                >
                                    <td className="p-2 font-mono text-zinc-500 whitespace-nowrap">{a.timestamp}</td>
                                    <td className="p-2 text-zinc-400">{a.actor}</td>
                                    <td className="p-2"><code className="font-bold text-white">{a.entry}</code></td>
                                    <td className="p-2">
                                        <span className="pill text-[9px] font-bold uppercase border bg-zinc-800 text-zinc-300 border-zinc-700">
                                            {a.action}
                                        </span>
                                    </td>
                                    <td className="p-2 text-zinc-400 whitespace-nowrap">
                                        {a.from || "—"} <span className="text-zinc-600">→</span>{" "}
                                        <code className="text-fuchsia-300">{a.to || "—"}</code>
                                    </td>
                                    <td className="p-2 text-zinc-500">{a.note || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ObligationReviewRow({ item, onDone }) {
    const STATES = ["compliant", "non-compliant", "investigating", "consult-professional", "not-applicable"];
    const [state, setState] = useState(item.compliance_state);
    const [notes, setNotes] = useState(item.notes || "");
    const [busy, setBusy] = useState(false);

    const submit = async (payload, okMsg) => {
        setBusy(true);
        try {
            await api.releaseOpsObligationsReview(payload);
            toast.success(okMsg);
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Review failed");
        } finally {
            setBusy(false);
        }
    };

    const changed = state !== item.compliance_state || (notes || "") !== (item.notes || "");

    return (
        <div
            className={`border rounded p-3 text-[11px] ${item.status === "overdue" ? "border-rose-700/50 bg-rose-950/10" : item.status === "current" ? "border-[#2a2a2a] bg-[#0d0d0d]" : "border-amber-700/40 bg-amber-950/10"}`}
            data-testid={`oblig-review-row-${item.name}`}
        >
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <code className="font-bold text-white">{item.name}</code>
                <span className="pill text-[9px] font-bold uppercase border bg-zinc-800 text-zinc-300 border-zinc-700">
                    {item.obligation_group}
                </span>
                <span className="pill text-[9px] font-bold uppercase border bg-cyan-900/40 text-cyan-200 border-cyan-700">
                    {item.compliance_state}
                </span>
                <span
                    className={`pill text-[9px] font-bold uppercase border ${item.status === "overdue" ? "bg-rose-900/40 text-rose-200 border-rose-700" : item.status === "current" ? "bg-zinc-800 text-zinc-400 border-zinc-700" : "bg-amber-900/40 text-amber-200 border-amber-700"}`}
                    data-testid={`oblig-review-status-${item.name}`}
                >
                    {item.status === "overdue" ? `Overdue ${Math.abs(item.days_until_due)}d` : item.status === "current" ? "Inside cadence" : `Due in ${item.days_until_due}d`}
                </span>
                <span className="ml-auto text-[9px] text-zinc-600 font-mono">
                    last review: {item.last_review || "never"} · every {item.cadence_days}d
                </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <select
                    value={state}
                    disabled={busy}
                    onChange={(e) => setState(e.target.value)}
                    data-testid={`oblig-review-state-select-${item.name}`}
                    className="brutal-input text-[10px] py-1 px-2 bg-zinc-900 border-zinc-700"
                >
                    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input
                    type="text"
                    value={notes}
                    disabled={busy}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notes (required on a state change)"
                    data-testid={`oblig-review-notes-input-${item.name}`}
                    className="brutal-input text-[10px] py-1 px-2 flex-1 min-w-[200px]"
                />
                <button
                    type="button"
                    disabled={busy || !changed}
                    onClick={() => submit(
                        { name: item.name, compliance_state: state, notes },
                        `${item.name} reviewed — ${state}`,
                    )}
                    data-testid={`oblig-review-save-btn-${item.name}`}
                    className="brutal-btn text-[10px] py-1 px-2.5 bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
                >
                    Save & mark reviewed
                </button>
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => submit(
                        { name: item.name },
                        `${item.name} — last review bumped to today`,
                    )}
                    data-testid={`oblig-review-nochange-btn-${item.name}`}
                    className="brutal-btn text-[10px] py-1 px-2.5 disabled:opacity-50"
                >
                    Mark reviewed — no change
                </button>
            </div>
        </div>
    );
}

function ObligationsReviewView() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    const load = () => {
        api.releaseOpsObligationsRegistry()
            .then(setData)
            .catch((e) => setError(e?.response?.data?.detail || "Failed to load obligations"));
    };
    useEffect(() => { load(); }, []);

    if (error) return <div className="text-xs text-rose-300" data-testid="oblig-review-error">{error}</div>;
    if (!data) return <p className="text-[11px] text-zinc-500 italic">Loading obligations…</p>;

    const queue = data.review_queue || [];
    // iter86.31.9 — On-demand clearing: every ACTIVE obligation is
    // actionable here, not just the ones the cadence says are due.
    const CADENCE = { "non-compliant": 0, "investigating": 90, "consult-professional": 90, "compliant": 180, "not-applicable": 365 };
    const queueNames = new Set(queue.map((q) => q.name));
    const others = (data.active_obligations || [])
        .filter((o) => !queueNames.has(o.name))
        .map((o) => ({
            name: o.name,
            obligation_group: o.obligation_group,
            compliance_state: o.compliance_state,
            notes: o.notes,
            last_review: o.last_review,
            cadence_days: CADENCE[o.compliance_state] ?? 180,
            status: "current",
            days_until_due: null,
        }));
    return (
        <div className="space-y-4" data-testid="stewardship-obligations-view">
            <div className="text-[10px] text-zinc-500">
                Cadences: 90d <code>investigating</code> / <code>consult-professional</code> · 180d <code>compliant</code> · 365d <code>not-applicable</code> · <code>non-compliant</code> always due.
                Queue shows items overdue or due within 30 days. If nothing changed, "Mark reviewed — no change" just bumps the last-review date.
            </div>
            {queue.length === 0 ? (
                <div
                    className="border border-dashed border-zinc-700 rounded-lg p-6 text-center text-xs text-zinc-500"
                    data-testid="oblig-review-empty"
                >
                    Nothing due for review.
                    {(data.counts?.total_active ?? 0) === 0
                        ? " No active obligations cataloged yet — the queue fills as entries land in obligations_registry.md."
                        : ` All ${data.counts.total_active} active obligations are inside their review cadence.`}
                </div>
            ) : (
                <div className="space-y-2" data-testid="oblig-review-queue">
                    {queue.map((q) => <ObligationReviewRow key={q.name} item={q} onDone={load} />)}
                </div>
            )}
            {others.length > 0 && (
                <div className="space-y-2" data-testid="oblig-review-all-active">
                    <div className="text-[10px] uppercase font-bold text-zinc-500 pt-1">
                        All active obligations — review or clear any item on demand
                    </div>
                    {others.map((q) => <ObligationReviewRow key={q.name} item={q} onDone={load} />)}
                </div>
            )}
        </div>
    );
}

export default function StewardshipTab() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    // iter87.61 Phase 0 · Step 1 — sub-tab URL persistence for Maestro.
    // `?sub=<key>` restores the sub-tab across reloads and deep-links.
    // Unknown / missing values fall through to "actions".
    const _VALID_SUBS = ["actions", "obligations", "tcl_mapping", "pulse_issues", "tech_debt", "vendor_todos", "severity", "audit"];
    const [searchParams, setSearchParams] = useSearchParams();
    const _initialSub = searchParams.get("sub");
    const [sub, _setSub] = useState(_VALID_SUBS.includes(_initialSub) ? _initialSub : "actions");
    const setSub = (next) => {
        _setSub(next);
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            if (next && next !== "actions") p.set("sub", next); else p.delete("sub");
            return p;
        }, { replace: false });
    };
    const [tclPending, setTclPending] = useState(0);
    const [dataPlane, setDataPlane] = useState(false);

    useEffect(() => {
        api.platformEnvironment().then((r) => setDataPlane(r.mode === "data_plane")).catch(() => {});
    }, []);

    useEffect(() => {
        api.tclMapping().then((r) => setTclPending(r?.pending_count || 0)).catch(() => {});
    }, [sub]);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await api.releaseOpsValuesRegistry());
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-4" data-testid="stewardship-tab">
            <div className="brutal-card p-4 border-2 border-amber-900/40 bg-amber-950/10">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <ClipboardList className="w-4 h-4 text-amber-300" />
                            <h2 className="font-display font-extrabold text-lg uppercase tracking-wide text-white">
                                Maestro — Registry Decisions
                            </h2>
                            {(data?.pending_count ?? 0) > 0 && (
                                <span
                                    className="pill text-[9px] font-bold uppercase border bg-amber-900/40 text-amber-200 border-amber-700"
                                    data-testid="stewardship-pending-badge"
                                >
                                    {data.pending_count} pending
                                </span>
                            )}
                            {tclPending > 0 && (
                                <span
                                    className="pill text-[9px] font-bold uppercase border bg-cyan-900/40 text-cyan-200 border-cyan-700"
                                    data-testid="stewardship-tcl-pending-badge"
                                >
                                    {tclPending} TCL pending
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-zinc-400">
                            Where classification decisions get made and recorded. Detail views live in the
                            Values and Obligations tabs; evaluation stays in Release Ops.
                        </div>
                        <div className="text-[10px] text-zinc-600 mt-1">
                            Coming next: "Map a Check" intake — route newly-added signals and CI scripts to a Value or Obligation.
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide bg-zinc-900 border border-zinc-700 rounded hover:text-white"
                        data-testid="stewardship-refresh-btn"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>
            </div>

            <SubTabs
                items={[
                    { k: "actions", label: "Actions", icon: ClipboardList },
                    { k: "obligations", label: "Obligations Review", icon: Scale },
                    { k: "tcl_mapping", label: "TCL Mapping", icon: Link2 },
                    { k: "pulse_issues", label: "Pulse Dispositions", icon: Radio },
                    { k: "tech_debt", label: "Tech Debt", icon: Wrench },
                    { k: "vendor_todos", label: "Vendors", icon: Handshake },
                    { k: "severity", label: "Severity", icon: ShieldAlert },
                    { k: "audit", label: "Audit", icon: History },
                ]}
                active={sub}
                onChange={setSub}
                testPrefix="stewardship-subtab"
            />

            {dataPlane && (
                <div className="border border-rose-800 rounded p-2.5 bg-rose-950/20 text-[11px] text-rose-200" data-testid="stewardship-data-plane-banner">
                    🔒 Production — Maestro is a read-only mirror here. All dispositions and classifications happen in the preview mezzanine and flow through git.
                </div>
            )}
            {error && (
                <div className="text-xs text-rose-300 bg-rose-950/40 border border-rose-800 rounded p-3" data-testid="stewardship-error">
                    {error}
                </div>
            )}
            {!data && !error && (
                <div className="text-xs text-zinc-500" data-testid="stewardship-loading">Loading…</div>
            )}

            {data && (
                <div className="brutal-card p-4">
                    {/* iter86.26 — data-plane read-only: TCL Mapping self-gates its
                        buttons; Audit is inherently read-only; the remaining write
                        surfaces are disabled wholesale (backend 409s regardless). */}
                    {sub === "actions" && <div className={dataPlane ? "pointer-events-none opacity-60" : ""}><ActionsView data={data} reload={load} /></div>}
                    {sub === "obligations" && <div className={dataPlane ? "pointer-events-none opacity-60" : ""}><ObligationsReviewView /></div>}
                    {sub === "tcl_mapping" && <TclMappingView />}
                    {sub === "pulse_issues" && <div className={dataPlane ? "pointer-events-none opacity-60" : ""}><PulseDispositionsView /></div>}
                    {sub === "tech_debt" && <div className={dataPlane ? "pointer-events-none opacity-60" : ""}><TechDebtDispositionView /></div>}
                    {/* iter86.52 — Vendor queue is preview-only by nav gating; no data-plane wrapper needed */}
                    {sub === "vendor_todos" && <VendorQueueView />}
                    {sub === "severity" && <div className={dataPlane ? "pointer-events-none opacity-60" : ""}><SeverityGovernanceView /></div>}
                    {sub === "audit" && <AuditView />}
                </div>
            )}
        </div>
    );
}
